import type { I18nKey, TParams } from '../i18n/index.ts';

/**
 * The ONE string this console uses to name an API key.
 *
 * Every page that lists keys used to pick its own fallback for a key
 * whose `name` is null, so a single unnamed key answered to three
 * different names at once — `—` on `/keys`, `Unnamed key` on
 * `/requests`, its prefix on `/usage` and `/dashboard`. An admin who
 * saw one of them spending tokens could not find it under either of the
 * other two (#47).
 *
 * The fallback is the PREFIX, and that choice is not free: platform-api
 * already builds the `/usage` key filter labels as
 * `key.name ?? key.keyPrefix ?? key.id`, and per cross-repo discipline
 * the console does not override a payload it did not author. So the
 * pages that do choose locally move to the prefix to match it, which
 * also keeps unnamed keys distinguishable from one another — a shared
 * placeholder would render every one of them as the same row.
 *
 * The placeholder is reached only when a key has NEITHER name nor
 * prefix, i.e. when there is nothing left to identify it with.
 *
 * WHERE THIS BELONGS: every slot that must NAME a key on its own —
 * a log row, a filter option, a usage row, a top-keys table. It does
 * NOT belong in a field explicitly captioned `Name` sitting next to a
 * field captioned `Prefix`; that pair states two attributes of the
 * record rather than naming the key, and an absent name there is `—`,
 * this console's mark for a field with no value. `/keys` is the only
 * such surface, which is also why it is the only page that can act on
 * an unnamed key: its row carries the rename form.
 *
 * @param t the caller's translator; this stays a plain function so
 *   module-scope helpers and table cells can share it.
 */
export function apiKeyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, params?: TParams) => string,
): string {
  if (name !== null && name.length > 0) return name;
  if (prefix !== null && prefix.length > 0) return prefix;
  return t('api_key.unnamed');
}

/**
 * The phrase a revoke confirmation makes the operator retype — the
 * key's name, or its prefix for a key that was never named (the same
 * fallback rule as console-web's `/keys` page, so the two consoles
 * demand the same characters for the same key). Shared by the `/keys`
 * revoke modal and the member-keys dialog on `/organization` so the
 * two revoke entry points cannot drift on what counts as "the name".
 *
 * A key with neither name nor prefix yields the empty string, which
 * `ConfirmDialog` treats as "no typing required" — there is nothing an
 * operator could meaningfully retype for such a key.
 */
export function apiKeyRevokePhrase(name: string | null, prefix: string): string {
  return name !== null && name.length > 0 ? name : prefix;
}
